import type { ItemVariation, MindItem, Product } from '@kimuno/api-sdk';
import React, { useCallback, useState } from 'react';

import { ManageItemInCollectionModal } from '../components/modals';
import type { Alert } from '../components/NotificationAlert/NotificationAlert';

type ShowFunc = (
	itemId: string,
	itemType: ItemVariation,
	itemData?: Partial<MindItem> | Partial<Product> | undefined,
	closeCallback?: () => void,
	onShowNotification?: (alert: Alert) => void
) => void;
const noop = () => undefined;

interface Context {
	isOpen: boolean;
	show: ShowFunc;
}

const defaultContext: Context = {
	isOpen: false,
	show: noop as ShowFunc,
};

const ManageItemInCollectionModalContext = React.createContext(defaultContext);

const ManageItemInCollectionModalProvider: React.FC = (props) => {
	const [itemId, setItemId] = useState('');
	const [itemData, setItemData] = useState({});
	const [itemType, setItemType] = useState<ItemVariation>('mindItem');
	const [isOpen, setIsOpen] = useState(false);
	const [closeCallback, setCloseCallback] = useState<(() => void) | undefined>(undefined);
	const [notificationCallback, setNotificationCallback] = useState<
		((alert: Alert) => void) | undefined
	>(undefined);

	const setModalConfig = (
		open: boolean,
		itemId?: string,
		itemType?: ItemVariation,
		itemData?: Partial<MindItem> | Partial<Product>,
		closeCallbackFunc?: () => void,
		notificationCallbackFunc?: (alert: Alert) => void
	) => {
		if (itemId) setItemId(itemId);
		if (itemType) setItemType(itemType);
		if (itemData) setItemData(itemData);
		if (closeCallbackFunc) {
			setCloseCallback(() => closeCallbackFunc);
		}
		if (notificationCallbackFunc) {
			setNotificationCallback(() => notificationCallbackFunc);
		}

		setIsOpen(open);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const show = useCallback(
		(
			itemId: string,
			itemType: ItemVariation,
			itemData: Partial<MindItem> | Partial<Product> | undefined,
			closeCallback?: () => void,
			onShowNotification?: (alert: Alert) => void
		) => {
			setModalConfig(true, itemId, itemType, itemData, closeCallback, onShowNotification);
		},
		[]
	);

	const onClose = () => {
		if (closeCallback) closeCallback();
		setModalConfig(false, '', 'mindItem', undefined, undefined, undefined);
	};

	return (
		<ManageItemInCollectionModalContext.Provider
			value={{
				show,
				isOpen,
			}}
		>
			<ManageItemInCollectionModal
				show={isOpen}
				onClose={onClose}
				itemId={itemId}
				itemData={itemData}
				itemType={itemType}
				onShowNotification={notificationCallback}
			/>
			{props.children}
		</ManageItemInCollectionModalContext.Provider>
	);
};

export { ManageItemInCollectionModalContext, ManageItemInCollectionModalProvider };
